import { useCallback, useContext, useEffect, useRef, useState, useMemo, useReducer } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import FilterListIcon from "@material-ui/icons/FilterList";
import { Button, useTheme } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import SearchIcon from "@material-ui/icons/Search";
import Collapse from "@material-ui/core/Collapse";
import ClearIcon from "@material-ui/icons/Clear";
import Tooltip from "@material-ui/core/Tooltip";
import Badge from "@material-ui/core/Badge";
import Paper from "@material-ui/core/Paper";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import SwitchCustom from "../../components/SwitchCustom/SwitchCustom";
import NewTicketModal from "../NewTicketModal";
import TicketsFilters from "../TicketsFilters";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

import { cleanupComponent, getSocket, onSocket } from "../../services/socket";

import DownloadIcon from "../../assets/icon/DownloadIcon";
import SuccessIcon from "../../assets/icon/SuccessIcon";
import AddIcon from "../../assets/icon/AddIcon";
import useTickets from "../../hooks/useTickets";
import { ticketsReducer } from "./ticketsReducer";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background: theme.palette.background.default,
    height: "100%",
    boxShadow: "none",
  },
  managerRoot: {
    borderRadius: "12px 16px 16px 0",
    position: "relative",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      borderRadius: 0,
      width: "100%",
    },
  },

  tabsHeader: {
    backgroundColor: theme.palette.background.default,
    borderRadius: "12px 12px 0 0",
    overflow: "hidden",
    boxShadow: "none",
    [theme.breakpoints.down("md")]: {
      borderRadius: 0,
      width: "100%",
    },
  },

  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: 8,
  },
  tabs: {
    minHeight: 48,
    height: 48,
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    paddingLeft: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(0.5),
    },
  },
  tabsList: {
    minHeight: 44,
    height: 44,
    background: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(1),
    },
  },
  tab: {
    minWidth: 84,
    minHeight: 48,
    textTransform: "none",
    fontSize: "0.95rem",
    fontWeight: 500,
    padding: theme.spacing(0.75, 1.5),
    [theme.breakpoints.down("md")]: {
      minWidth: 70,
      fontSize: "0.85rem",
      padding: theme.spacing(0.5, 1),
    },
  },
  subTab: {
    minWidth: 84,
    minHeight: 44,
    textTransform: "none",
    fontSize: "0.9625rem",
    fontWeight: 500,
    padding: theme.spacing(0.5, 1.25),
  },
  tabWrapper: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    "& > *": {
      marginRight: theme.spacing(0.75),
      marginBottom: "0 !important",
    },
    "& > *:first-child": {
      marginRight: theme.spacing(0.75),
    },
  },

  tabWrapperList: {
    justifyContent: "left",
    alignItems: "center",
    flexDirection: "row",
    "& > *:first-child": {
      marginRight: theme.spacing(1),
    },
    marginRight: theme.spacing(1),
  },

  ticketOptionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 1.5),
    background: theme.palette.background.default,
    minHeight: 56,
    gap: theme.spacing(1),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(0.75, 1),
      minHeight: 48,
    },
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flex: 1,
    maxWidth: '70%',
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(1),
    }
  },
  centerSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(0),
      marginLeft: theme.spacing(0.5),
      gap: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.8),
      marginLeft: theme.spacing(0.3),
      marginRight: theme.spacing(0.3),
    },
  },
  buttonNew: {
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    padding: theme.spacing(0.5, 1.75),
    height: 36,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    whiteSpace: "nowrap",
    minWidth: "fit-content",
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(0.4, 1.25),
      fontSize: 13,
    },
  },
  switchLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    transition: 'all 0.2s ease',
    fontWeight: 'bold',
    [theme.breakpoints.down('lg')]: {
      width: 0,
      opacity: 0,
      overflow: 'hidden',
      margin: 0,
      padding: 0,
      position: 'absolute',
    }
  },

  serachInputWrapper: {
    display: "flex",
    alignItems: "center",
    borderRadius: 8,
    padding: "0 8px",
    height: 36,
    width: "100%",
    minWidth: 160,
    background: theme.palette.tabHeaderBackground,
    border: `1px solid ${theme.mode === "light" ? "rgba(0, 0, 0, 0.10)" : "rgba(255, 255, 255, 0.12)"}`,
    boxShadow: "none",
    [theme.breakpoints.down("md")]: {
      minWidth: 130,
    },
  },

  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },

  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
  },

  badge: {
    right: "-8px",
  },
  openCountBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: theme.palette.primary.main,
      color: theme.mode === "light" ? "#1B1B18" : "#1B1B18",
      fontWeight: 700,
      fontSize: 12,
      minWidth: 22,
      height: 20,
      padding: "0 6px",
    },
  },
  pendingCountBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#F2994A",
      color: "#FFFFFF",
      fontWeight: 700,
      fontSize: 12,
      minWidth: 22,
      height: 20,
      padding: "0 6px",
    },
  },
  show: {
    display: "block",
  },
  hide: {
    display: "none !important",
  },

  filterContainer: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 2,
    marginTop: theme.spacing(1),
    background: theme.palette.background.default,
  },

  filtersWrapper: {
    position: "relative",
  },

  searchSection: {
    display: "flex",
    alignItems: "center",
    maxWidth: 300,
    flex: 1,
  },
  filterAndSwitchSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(1),
    }
  },

  filterButton: {
    minWidth: "unset",
    width: 36,
    height: 36,
    padding: 0,
    borderRadius: 8,
    backgroundColor: (isFilterOpen) => (isFilterOpen ? theme.palette.primary.main : "transparent"),
    color: (isFilterOpen) => (isFilterOpen ? theme.palette.common.white : theme.palette.primary.main),
    border: `1px solid ${theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor: (isFilterOpen) => (isFilterOpen ? theme.palette.primary.dark : "rgba(241, 176, 0, 0.12)"),
    },
    [theme.breakpoints.down("sm")]: {
      width: 32,
      height: 32,
    },
  },

  switchSection: {
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    position: "relative",
    marginRight: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
}));

const TICKET_STATUSES = {
  OPEN: "open",
  PENDING: "pending",
  CLOSED: "closed",
};

const EMPTY_FILTERS = {
  tags: [],
  users: [],
  queueIds: [],
  connections: [],
  queueId: '',
  dateFrom: '',
  dateTo: '',
  status: '',
  searchParam: '',
};

const getItemId = (item) => (typeof item === "object" ? item.id : item);

const normalizeIds = (items) => (
  Array.isArray(items)
    ? items.map(getItemId).filter((id) => id !== undefined && id !== null)
    : []
);

const getQueueIdsFromFilters = (filters) => {
  if (Array.isArray(filters.queueIds)) {
    return filters.queueIds;
  }

  if (filters.queueIds !== undefined && filters.queueIds !== '') {
    return [Number(filters.queueIds)];
  }

  return [];
};

const areFiltersEmpty = (filters) => (
  (!filters.tags || filters.tags.length === 0) &&
  (!filters.users || filters.users.length === 0) &&
  (!filters.queueIds || filters.queueIds.length === 0) &&
  (!filters.connections || filters.connections.length === 0) &&
  (!filters.dateFrom || filters.dateFrom === '') &&
  (!filters.dateTo || filters.dateTo === '') &&
  (!filters.status || filters.status === '')
);

const normalizeFilters = (filters) => ({
  tags: normalizeIds(filters.tags),
  users: normalizeIds(filters.users),
  queueIds: getQueueIdsFromFilters(filters),
  connections: normalizeIds(filters.connections),
  dateFrom: filters.dateFrom || '',
  dateTo: filters.dateTo || '',
  status: filters.status || '',
});

const getAssignedUserId = (ticket) => ticket.userId || ticket.user?.id;

const isDateBefore = (date, referenceDate) => new Date(date) < new Date(referenceDate);

const isDateAfter = (date, referenceDate) => new Date(date) > new Date(referenceDate);

const getTicketFilterMismatch = (ticket, filters) => {
  if (!ticket) {
    return { reason: "Ticket invalido", details: { ticket } };
  }

  const assignedUserId = getAssignedUserId(ticket);
  const ticketTagIds = (ticket.tags || []).map(({ id }) => id);

  if (
    ticket.status !== TICKET_STATUSES.PENDING &&
    filters.profile === "admin" &&
    filters.effectiveShowAll &&
    assignedUserId !== filters.userId
  ) {
    return {
      reason: "No cumple por usuario (admin sin showAll)",
      details: { assignedUserId, userId: filters.userId, ticketId: ticket.id },
    };
  }

  if (filters.statusFilter && ticket.status !== filters.statusFilter) {
    return {
      reason: "No cumple por status",
      details: { ticketStatus: ticket.status, statusFilter: filters.statusFilter, ticketId: ticket.id },
    };
  }

  if (filters.queueId && ticket.queueId !== filters.queueId) {
    return {
      reason: "No cumple por queueId",
      details: { ticketQueueId: ticket.queueId, queueId: filters.queueId, ticketId: ticket.id },
    };
  }

  if (filters.selectedQueueIds.length > 0 && !filters.selectedQueueIds.includes(ticket.queueId)) {
    return {
      reason: "No cumple por selectedQueueIds",
      details: { ticketQueueId: ticket.queueId, selectedQueueIds: filters.selectedQueueIds, ticketId: ticket.id },
    };
  }

  if (filters.selectedUserIds.length > 0 && !filters.selectedUserIds.includes(assignedUserId)) {
    return {
      reason: "No cumple por selectedUserIds",
      details: { assignedUserId, selectedUserIds: filters.selectedUserIds, ticketId: ticket.id },
    };
  }

  if (
    filters.selectedTagIds.length > 0 &&
    !filters.selectedTagIds.some((id) => ticketTagIds.includes(id))
  ) {
    return {
      reason: "No cumple por tags",
      details: { ticketTagIds, selectedTagIds: filters.selectedTagIds, ticketId: ticket.id },
    };
  }

  if (
    filters.selectedConnectionIds.length > 0 &&
    !filters.selectedConnectionIds.includes(ticket.connectionId)
  ) {
    return {
      reason: "No cumple por connectionId",
      details: {
        ticketConnectionId: ticket.connectionId,
        selectedConnectionIds: filters.selectedConnectionIds,
        ticketId: ticket.id,
      },
    };
  }

  if (filters.dateFrom && isDateBefore(ticket.updatedAt, filters.dateFrom)) {
    return {
      reason: "No cumple por dateFrom",
      details: { ticketDate: ticket.updatedAt, dateFrom: filters.dateFrom, ticketId: ticket.id },
    };
  }

  if (filters.dateTo && isDateAfter(ticket.updatedAt, filters.dateTo)) {
    return {
      reason: "No cumple por dateTo",
      details: { ticketDate: ticket.updatedAt, dateTo: filters.dateTo, ticketId: ticket.id },
    };
  }

  return null;
};

const TicketsManagerTabs = () => {
  const history = useHistory();
  const theme = useTheme();

  const [inputValue, setInputValue] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const { ticketId: routeUuid } = useParams();
  
  // Estado inicial sin localStorage - por defecto true para todos los usuarios
  const [showAllTickets, setShowAllTickets] = useState(false);
  const { user } = useContext(AuthContext);
  const { profile } = user;

  // Permitir showAll=true para todos los usuarios
  const effectiveShowAll = showAllTickets;

  // Función para cambiar el estado del switch (sin localStorage)
  const handleShowAllTicketsChange = useCallback(() => {
    // Permitir cambio para todos los usuarios
    setShowAllTickets((prevState) => !prevState);
  }, []);

  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedConnectionIds, setSelectedConnectionIds] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [queueId, setQueueId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const classes = useStyles(isFilterOpen);

  // Agregar estos refs al principio del componente
  const filtersTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null); // Usar ref para el timeout de búsqueda
  const [openTickets, dispatchOpen] = useReducer(ticketsReducer, []);
  const [pendingTickets, dispatchPending] = useReducer(ticketsReducer, []);
  const [closedTickets, dispatchClosed] = useReducer(ticketsReducer, []);
  const [searchTickets, dispatchSearch] = useReducer(ticketsReducer, []);

  const dispatchByStatus = useMemo(() => ({
    [TICKET_STATUSES.OPEN]: dispatchOpen,
    [TICKET_STATUSES.PENDING]: dispatchPending,
    [TICKET_STATUSES.CLOSED]: dispatchClosed,
  }), []);

  const getDispatchByStatus = useCallback(
    (status) => dispatchByStatus[status] || null,
    [dispatchByStatus]
  );

  const dispatchToAllTicketLists = useCallback((action) => {
    dispatchOpen(action);
    dispatchPending(action);
    dispatchClosed(action);
    dispatchSearch(action);
  }, []);

  const deleteTicketFromLists = useCallback((ticketId) => {
    dispatchToAllTicketLists({
      type: "DELETE_TICKET",
      payload: ticketId,
    });
  }, [dispatchToAllTicketLists]);

  const componentId = useMemo(() => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `ticketsList-${timestamp}-${random}`;
  }, []);

  const commonFilters = useMemo(() => ({
    searchParam,
    showAll: effectiveShowAll,
    tags: selectedTagIds,
    users: selectedUserIds,
    connections: selectedConnectionIds,
    queueIds: selectedQueueIds,
    queueId,
    dateFrom,
    dateTo,
  }), [
    searchParam,
    effectiveShowAll,
    selectedTagIds,
    selectedUserIds,
    selectedConnectionIds,
    selectedQueueIds,
    queueId,
    dateFrom,
    dateTo
  ]);

  // Ref para evitar múltiples cargas simultáneas 
  const [pageNumber, setPageNumber] = useState(1);
  const [openPage, setOpenPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [closedPage, setClosedPage] = useState(1);

  const openTicketsData = useTickets({
    ...commonFilters,
    status: TICKET_STATUSES.OPEN,
    pageNumber: openPage,
  });
  const pendingTicketsData = useTickets({
    ...commonFilters,
    status: TICKET_STATUSES.PENDING,
    pageNumber: pendingPage,
  });
  const closedTicketsData = useTickets({
    ...commonFilters,
    status: TICKET_STATUSES.CLOSED,
    pageNumber: closedPage,
  });
  const searchTicketData = useTickets({
    ...commonFilters,
    pageNumber,
  });

  // Agregar un ref para tener acceso a los componentes de tickets
  const ticketsOpenRef = useRef(null);
  const ticketsPendingRef = useRef(null);
  const ticketsClosedRef = useRef(null);
  const ticketsSearchRef = useRef(null);
  const routeUuidRef = useRef(routeUuid);
  const filtersRef = useRef({});
  
  useEffect(() => {
    routeUuidRef.current = routeUuid;
  }, [routeUuid]);

  useEffect(() => {
    filtersRef.current = {
      effectiveShowAll,
      selectedTagIds,
      selectedQueueIds,
      selectedUserIds,
      selectedConnectionIds,
      statusFilter,
      queueId,
      dateFrom,
      dateTo,
      userId: user.id,
      profile
    };
  }, [
    effectiveShowAll,
    selectedTagIds,
    selectedQueueIds,
    selectedUserIds,
    selectedConnectionIds,
    statusFilter,
    queueId,
    dateFrom,
    dateTo,
    user.id,
    profile
  ]);

  const doesTicketMatchFilters = useCallback((ticket) => {
    const mismatch = getTicketFilterMismatch(ticket, filtersRef.current);

    if (mismatch) {
      return false;
    }

    return true;
  }, []);

  const closeActiveChat = useCallback((ticketUuid) => {
    if (ticketUuid && ticketUuid === routeUuidRef.current) {
      history.push("/tickets");
    }
  }, [history]);

  const handleTicketRemove = useCallback(({ ticketId, ticket: { uuid } }) => {
    if (!ticketId) return;

    closeActiveChat(uuid);
    deleteTicketFromLists(ticketId);
  }, [closeActiveChat, deleteTicketFromLists]);

  const handleTicketUpdate = useCallback((data) => {
    if (!data?.action) return;

    const { action, ticket } = data;

    const beforeStatus = data.beforeStatus;
    const beforeUserId = data.beforeUserId;
    const beforeQueueId = data.beforeQueueId;
    const ticketStatus = data.ticketStatus || ticket?.status;
    const cantSeeUsers = data.cantSeeUsers;
    const meta = data.metadata || {};
    const beforeStatusAny = beforeStatus ?? meta.beforeStatus;
    const beforeUserIdAny = beforeUserId ?? meta.beforeUserId;
    const beforeQueueIdAny = beforeQueueId ?? meta.beforeQueueId;

    // Filtro de visibilidad: el usuario actual está en cantSeeUsers → no debe ver/actualizar
    const isHiddenForCurrentUser =
      Array.isArray(cantSeeUsers) && cantSeeUsers.includes(user.id);

    const newStatus = ticket?.status ?? ticketStatus;
    const oldDispatch = getDispatchByStatus(beforeStatusAny);
    const newDispatch = getDispatchByStatus(newStatus);
    const matchesFilters = ticket ? doesTicketMatchFilters(ticket) : true;

    // UPDATE
    if (action === "resolved") {
      // Remover de la lista del status anterior
      oldDispatch({
        type: "DELETE_TICKET",
        payload: ticket.id,
      });
      // Insertar / actualizar en la lista del status actual
      newDispatch?.({
        type: "UPDATE_TICKET",
        payload: ticket,
      });

      dispatchSearch({
        type: "UPDATE_TICKET",
        payload: ticket,
      });
      return;
    }
    
    if (action === "update" && ticket) {
      // Si el usuario actual no puede ver este ticket, salir del chat si lo tenía
      // abierto y removerlo de todas las listas.
      if (isHiddenForCurrentUser) {
        closeActiveChat(ticket.uuid);
        deleteTicketFromLists(ticket.id);
        return;
      }

      // Detección de cierre: el ticket pasó de open/pending a closed.
      // El "closeActiveChat" recibe uuid (que es lo que matchea el path actual).
      const wasJustClosed =
        beforeStatusAny &&
        beforeStatusAny !== TICKET_STATUSES.CLOSED &&
        newStatus === TICKET_STATUSES.CLOSED;
      if (wasJustClosed) {
        closeActiveChat(ticket.uuid);
      }

      // Hubo cambio de status (aceptar / rechazar / resolver / reabrir):
      // borrar de la lista del status anterior y meter en la nueva.
      const hasStatusChanged =
        beforeStatusAny && beforeStatusAny !== newStatus;

      if (hasStatusChanged && oldDispatch) {
        // Remover de la lista del status anterior
        oldDispatch({
          type: "DELETE_TICKET",
          payload: ticket.id,
        });
      }

      const hasQueueChanged =
        beforeQueueIdAny !== undefined &&
        beforeQueueIdAny !== ticket?.queueId;

      const hasUserChanged =
        beforeUserIdAny !== undefined &&
        beforeUserIdAny !== ticket?.userId;

      const ownershipOrQueueChanged =
        hasQueueChanged || hasUserChanged;

      if (!hasStatusChanged && ownershipOrQueueChanged && !matchesFilters) {
        // El ticket cambió de queue o user, no cumple los filtros actuales:
        // removerlo de TODAS las listas para evitar que quede "huérfano".
        deleteTicketFromLists(ticket.id);
        return;
      }

      if (!matchesFilters) {
        // Si no pasa los filtros actuales, asegurarnos de sacarlo también
        // de la lista destino (por si estaba ahí de una carga anterior).
        newDispatch?.({
          type: "DELETE_TICKET",
          payload: ticket.id,
        });
        return;
      }

      // Insertar / actualizar en la lista del status actual
      newDispatch?.({
        type: "UPDATE_TICKET",
        payload: ticket,
      });

      dispatchSearch({
        type: "UPDATE_TICKET",
        payload: ticket,
      });

      return;
    }

    // CREATE / ADD
    if ((action === "create" || action === "add") && ticket) {
      if (isHiddenForCurrentUser) return;

      // Por consistencia: si venía en otra lista antes, sacarlo
      if (beforeStatusAny && beforeStatusAny !== newStatus) {
        oldDispatch?.({
          type: "DELETE_TICKET",
          payload: ticket.id,
        });
      }

      if (!matchesFilters) return;

      newDispatch?.({
        type: "UPDATE_TICKET",
        payload: ticket,
        showAsNew: action === "add",
      });

      return;
    }

    // DELETE
    if ((action === "delete" || action === "remove") && (ticket?.id || data.ticketId)) {
      const ticketId = ticket?.id || data.ticketId;
      closeActiveChat(ticket?.uuid);
      deleteTicketFromLists(ticketId);
      return;
    }

  }, [
    closeActiveChat,
    deleteTicketFromLists,
    doesTicketMatchFilters,
    getDispatchByStatus,
    user.id
  ]);

  const handleUserQueuesUpdate = useCallback(() => {}, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    getSocket(companyId);

    onSocket("ticket:resolved", handleTicketUpdate, componentId);
    onSocket("ticket:update", handleTicketUpdate, componentId);
    onSocket("ticket:remove", handleTicketRemove, componentId);
    onSocket("ticket:transferred", handleTicketUpdate, componentId);
    onSocket("ticket:accepted", handleTicketUpdate, componentId);
    onSocket("ticket:reopened", handleTicketUpdate, componentId);
    onSocket(`company-${companyId}-user-queues-updated`, handleUserQueuesUpdate, componentId);

    return () => {
      cleanupComponent(componentId);
    };
  }, [
    componentId,
    handleTicketRemove,
    handleTicketUpdate,
    handleUserQueuesUpdate
  ]);

  useEffect(() => {
    dispatchOpen({
      type: openPage === 1 ? "LOAD_TICKETS" : "APPEND_TICKETS",
      payload: openTicketsData.tickets,
    });
  }, [openTicketsData.tickets, openPage]);

  useEffect(() => {
    dispatchPending({
      type: pendingPage === 1 ? "LOAD_TICKETS" : "APPEND_TICKETS",
      payload: pendingTicketsData.tickets,
    });
  }, [pendingTicketsData.tickets, pendingPage]);

  useEffect(() => {
    dispatchClosed({
      type: closedPage === 1 ? "LOAD_TICKETS" : "APPEND_TICKETS",
      payload: closedTicketsData.tickets,
    });
  }, [closedTicketsData.tickets, closedPage]);

  useEffect(() => {
    dispatchSearch({
      type: pageNumber === 1 ? "LOAD_TICKETS" : "APPEND_TICKETS",
      payload: searchTicketData.tickets,
    });
  }, [searchTicketData.tickets, pageNumber]);

  useEffect(() => {
    setOpenPage(1);
    dispatchOpen({ type: "RESET" });
  }, [
    selectedTagIds,
    selectedUserIds,
    selectedQueueIds,
    selectedConnectionIds,
    dateFrom,
    dateTo,
    statusFilter,
    searchParam,
    effectiveShowAll,
    openPage
  ]);

  useEffect(() => {
    setClosedPage(1);
    dispatchClosed({ type: "RESET" });
  }, [
    selectedTagIds,
    selectedUserIds,
    selectedQueueIds,
    selectedConnectionIds,
    dateFrom,
    dateTo,
    statusFilter,
    searchParam,
    closedPage
  ]);

  useEffect(() => {
    setPendingPage(1);
    dispatchPending({ type: "RESET" });
  }, [
    selectedTagIds,
    selectedUserIds,
    selectedQueueIds,
    selectedConnectionIds,
    dateFrom,
    dateTo,
    statusFilter,
    searchParam,
    pendingPage
  ]);

  const clearSearchTimeout = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, []);

  // Función para forzar recarga de tickets en todas las pestañas
  const forceReloadTickets = useCallback(() => {
    if (tab === "open") {
      if (tabOpen === "open" && ticketsOpenRef.current?.reloadTickets) {
        ticketsOpenRef.current.reloadTickets();
      } else if (tabOpen === "pending" && ticketsPendingRef.current?.reloadTickets) {
        ticketsPendingRef.current.reloadTickets();
      }
    } else if (tab === "closed" && ticketsClosedRef.current?.reloadTickets) {
      ticketsClosedRef.current.reloadTickets();
    } else if (tab === "search" && ticketsSearchRef.current?.reloadTickets) {
      ticketsSearchRef.current.reloadTickets();
    }
  }, [tab, tabOpen]);

  const reloadTicketsSoon = useCallback(() => {
    setTimeout(() => {
      forceReloadTickets();
    }, 50);
  }, [forceReloadTickets]);

  const handleSearch = (e) => {
    const value = e.target.value;
    const trimmedValue = value.trim();
    setInputValue(value);

    clearSearchTimeout();

    // Si el valor está vacío, aplicar inmediatamente sin esperar
    if (trimmedValue === '') {
      setSearchParam('');
      reloadTicketsSoon();
      return;
    }

    // Para otros valores, aplicar debounce pero con un umbral más corto para respuesta rápida
    searchTimeoutRef.current = setTimeout(() => {
      setSearchParam(trimmedValue);
      searchTimeoutRef.current = null;
    }, 300); // Reducir el tiempo de debounce para mayor responsividad
  };

  const handleClearSearch = () => {
    clearSearchTimeout();
    
    setInputValue("");
    setSearchParam("");
    reloadTicketsSoon();
  };

  // Limpiar los timeouts al desmontar
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (filtersTimeoutRef.current) {
        clearTimeout(filtersTimeoutRef.current);
      }
    };
  }, []);

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    setTabOpen(newValue);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket && ticket.uuid) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const applyFilterState = (filters) => {
    setSelectedTagIds(filters.tags);
    setSelectedUserIds(filters.users);
    setSelectedConnectionIds(filters.connections);
    setSelectedQueueIds(filters.queueIds);
    setQueueId(filters.queueIds.length === 1 ? filters.queueIds[0] : '');
    setDateFrom(filters.dateFrom);
    setDateTo(filters.dateTo);
    setStatusFilter(filters.status);
  };

  const resetFilterState = () => {
    applyFilterState(EMPTY_FILTERS);
    setSearchParam(EMPTY_FILTERS.searchParam);
  };

  const handleFiltersChange = (filters) => {
    // Cerrar el panel de filtros inmediatamente
    setIsFilterOpen(false);
    
    if (areFiltersEmpty(filters)) {
      resetFilterState();
      return;
    }
    
    const normalizedFilters = normalizeFilters(filters);

    applyFilterState(normalizedFilters);
  };

  const handleFilterClick = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  return (
    <div className={classes.managerRoot}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <Paper square elevation={0} className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="standard"
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}
        >
          <Tab
            value={"open"}
            icon={<DownloadIcon fill={theme.palette.messageIcons} strokeWidth="1.8" />}
            label={i18n.t("tickets.tabs.open.title")}
            classes={{ root: classes.tab, wrapper: classes.tabWrapper }}
          />
         
          <Tab
            value={"closed"}
            icon={<SuccessIcon fill={theme.palette.messageIcons} strokeWidth="1.8" />}
            label={i18n.t("tickets.tabs.closed.title")}
            classes={{ root: classes.tab, wrapper: classes.tabWrapper }}
          />

          <Tab
            value={"search"}
            icon={<SearchIcon style={{ color: theme.palette.messageIcons }} />}
            label={i18n.t("tickets.tabs.search.title")}
            classes={{ root: classes.tab, wrapper: classes.tabWrapper }}
          />
        </Tabs>
      </Paper>
      <div className={classes.filtersWrapper}>
        <Paper 
          square 
          elevation={0} 
          className={classes.ticketOptionsBox}
        >
          <div className={classes.leftSection}>
            <div className={classes.searchSection}>
              <Tooltip title="Buscar tickets" placement="top">
                <Paper className={classes.serachInputWrapper}>
                  <SearchIcon className={classes.searchIcon} />
                  <InputBase
                    className={classes.searchInput}
                    placeholder={i18n.t("tickets.search.placeholder")}
                    value={inputValue}
                    onChange={handleSearch}
                    inputProps={{
                      'aria-label': 'buscar tickets',
                      style: { padding: "6px 0" }
                    }}
                    endAdornment={
                      inputValue ? (
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          style={{ padding: 8 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : null
                    }
                  />
                </Paper>
              </Tooltip>
            </div>

            <div className={classes.filterAndSwitchSection}>
              <Tooltip title="Filtrar tickets" placement="top">
                <IconButton
                  onClick={handleFilterClick}
                  className={classes.filterButton}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
              {/* Mostrar el switch "Ver todos" a todos los usuarios */}
              {
                profile === 'admin' && (
                  <div className={classes.switchSection}>
                    <Tooltip title="Mostrar solo los tickets asignados a ti" placement="top">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <SwitchCustom
                          checked={showAllTickets}
                          onChange={handleShowAllTicketsChange}
                          name="showAllTickets"
                          disabled={openTicketsData.loading}
                        />
                        <span className={classes.switchLabel}>
                          {i18n.t("tickets.buttons.showAll")}
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                ) 
              }
            </div>
          </div>

          <Tooltip title="Crear un nuevo ticket" placement="top">
            <Button
              variant="outlined"
              className={classes.buttonNew}
              onClick={() => setNewTicketModalOpen(true)}
              startIcon={<AddIcon fill={theme.palette.primary.main} className={classes.iconAdd} />}
            >
              {i18n.t("ticketsManager.buttons.newTicket")}
            </Button>
          </Tooltip>
        </Paper>

        <Collapse in={isFilterOpen}>
          <Paper className={classes.filterContainer} elevation={0}>
            <TicketsFilters 
              onFilterChange={handleFiltersChange}
              showStatusFilter={tab === "search"}
              isAdmin={profile === "admin"}
              companyId={user.companyId}
              forceReloadTickets={forceReloadTickets}
            />
          </Paper>
        </Collapse>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TabPanel value={tab} name="open" >
          <Tabs
            value={tabOpen}
            onChange={handleChangeTabOpen}
            indicatorColor="primary"
            textColor="primary"
            variant="standard"
            className={classes.tabsList}
          >
            <Tab
              label={
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span>{i18n.t("ticketsList.assignedHeader")}</span>
                  <Badge
                    className={`${classes.badge} ${classes.openCountBadge}`}
                    badgeContent={openTickets?.length}
                    max={999}
                    style={{ marginLeft: 10 }}
                  />
                </div>
              }
              value={"open"}
              classes={{ root: classes.subTab, wrapper: classes.tabWrapperList }}
            />
            <Tab
              label={
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span>{i18n.t("ticketsList.pendingHeader")}</span>
                  <Badge
                    className={`${classes.badge} ${classes.pendingCountBadge}`}
                    badgeContent={pendingTickets?.length}
                    max={999}
                    style={{ marginLeft: 10 }}
                  />
                </div>
              }
              value={"pending"}
              classes={{ root: classes.subTab, wrapper: classes.tabWrapperList }}
            />
          </Tabs>
        </TabPanel>
        <TabPanel value={tab} name="open" className={classes.ticketsWrapper} style={{ flex: 1, overflow: 'hidden' }}>
          <Paper elevation={0} className={classes.ticketsWrapper} style={{ height: "100%" }}>
            {tabOpen === "open" && (
              <TicketsList
                tickets={openTickets}
                hasMore={openTicketsData.hasMore}
                loading={openTicketsData.loading}
                dispatch={dispatchOpen}
                setPageNumber={setOpenPage}
              />
            )}
             
            {tabOpen === "pending" && (
              <TicketsList
                tickets={pendingTickets}
                hasMore={pendingTicketsData.hasMore}
                loading={pendingTicketsData.loading}
                dispatch={dispatchPending}
                setPageNumber={setPendingPage}
              />
            )}
          </Paper>
        </TabPanel>
        <TabPanel value={tab} name="closed" className={classes.ticketsWrapper} style={{ flex: 1, overflow: 'hidden' }}>
          <TicketsList
            tickets={closedTickets}
            hasMore={closedTicketsData.hasMore}
            loading={closedTicketsData.loading}
            dispatch={dispatchClosed}
            setPageNumber={setClosedPage}
          />
        </TabPanel>
        <TabPanel value={tab} name="search" className={classes.ticketsWrapper} style={{ flex: 1, overflow: 'hidden' }}>
          <TicketsList
            tickets={searchTickets}
            hasMore={searchTicketData.hasMore}
            loading={searchTicketData.loading}
            dispatch={dispatchSearch}
            setPageNumber={setPageNumber}
          />
        </TabPanel>
      </div>
    </div>
  );
};

export default TicketsManagerTabs;
